import React, { useState, useEffect } from 'react';
import { Save, Building, Phone, Mail, MapPin, Clock, Wrench, Volume2, Globe, Loader2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_OPTIONS = [
  { value: 'alloy', label: 'Alloy', description: 'Neutral, balanced' },
  { value: 'ash', label: 'Ash', description: 'Clear, articulate' },
  { value: 'coral', label: 'Coral', description: 'Warm, friendly' },
  { value: 'echo', label: 'Echo', description: 'Smooth, calm' },
  { value: 'fable', label: 'Fable', description: 'Expressive, storytelling' },
  { value: 'nova', label: 'Nova', description: 'Energetic, upbeat (female)' },
  { value: 'onyx', label: 'Onyx', description: 'Deep, authoritative (male)' },
  { value: 'sage', label: 'Sage', description: 'Wise, measured' },
  { value: 'shimmer', label: 'Shimmer', description: 'Bright, cheerful (female)' },
];

const BUSINESS_TYPES = [
  { value: 'mechanic', label: 'Mechanic / Auto Repair', icon: '🔧' },
  { value: 'barber', label: 'Barbershop', icon: '💈' },
  { value: 'salon', label: 'Hair Salon / Beauty', icon: '💇' },
  { value: 'pizza', label: 'Pizza Shop', icon: '🍕' },
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'dental', label: 'Dental Office', icon: '🦷' },
  { value: 'medical', label: 'Medical Office', icon: '🏥' },
  { value: 'spa', label: 'Spa / Wellness', icon: '💆' },
  { value: 'gym', label: 'Gym / Fitness', icon: '💪' },
  { value: 'retail', label: 'Retail Store', icon: '🛍️' },
  { value: 'other', label: 'Other Business', icon: '🏢' },
];

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parsingMaps, setParsingMaps] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    hours: '',
    services: [],
    google_maps_url: '',
    business_type: 'mechanic',
    voice: 'onyx',
    additional_info: ''
  });
  const [servicesText, setServicesText] = useState('');
  const [mapsUrl, setMapsUrl] = useState('');

  useEffect(() => {
    fetchBusinessInfo();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API}/business-info`);
      setBusinessInfo(response.data);
      setServicesText(response.data.services?.join('\n') || '');
      setMapsUrl(response.data.google_maps_url || '');
    } catch (error) {
      console.error('Error fetching business info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...businessInfo,
        services: servicesText.split('\n').map(s => s.trim()).filter(s => s)
      };
      
      await axios.put(`${API}/business-info`, data);
      toast.success('Settings saved');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGoogleMapsImport = async () => {
    if (!mapsUrl) {
      toast.error('Please enter a Google Maps URL');
      return;
    }
    
    setParsingMaps(true);
    
    try {
      const response = await axios.post(`${API}/business-info/from-google-maps`, {
        url: mapsUrl
      });
      
      if (response.data.success) {
        const info = response.data.business_info;
        setBusinessInfo(info);
        setServicesText(info.services?.join('\n') || '');
        toast.success(`Imported: ${info.name}`);
      }
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Failed to import from Google Maps');
    } finally {
      setParsingMaps(false);
    }
  };

  const selectedVoice = VOICE_OPTIONS.find(v => v.value === businessInfo.voice);
  const selectedType = BUSINESS_TYPES.find(t => t.value === businessInfo.business_type);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
          BUSINESS SETTINGS
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure your AI receptionist for any business type
        </p>
      </div>

      {/* Google Maps Import */}
      <Card className="card-modern mb-6">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-xl font-bold text-foreground tracking-tight flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#10B981]" />
            QUICK SETUP FROM GOOGLE MAPS
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-muted-foreground mb-4">
            Paste your Google Maps business link and we'll automatically fill in the details
          </p>
          <div className="flex gap-3">
            <Input
              value={mapsUrl}
              onChange={(e) => setMapsUrl(e.target.value)}
              placeholder="https://maps.google.com/... or https://goo.gl/maps/..."
              className="input-industrial flex-1"
              data-testid="google-maps-input"
            />
            <Button 
              onClick={handleGoogleMapsImport}
              disabled={parsingMaps}
              className="btn-primary px-6"
              data-testid="import-maps-btn"
            >
              {parsingMaps ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Import
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Business Type & Voice */}
          <Card className="card-modern">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground tracking-tight flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-[#FF3B30]" />
                AI CONFIGURATION
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Business Type</label>
                <Select 
                  value={businessInfo.business_type} 
                  onValueChange={(v) => setBusinessInfo({...businessInfo, business_type: v})}
                >
                  <SelectTrigger className="input-industrial" data-testid="business-type-select">
                    <SelectValue>
                      {selectedType && (
                        <span className="flex items-center gap-2">
                          <span>{selectedType.icon}</span>
                          <span>{selectedType.label}</span>
                        </span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {BUSINESS_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-foreground hover:bg-[#27272A]">
                        <span className="flex items-center gap-2">
                          <span>{type.icon}</span>
                          <span>{type.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[#6B7280] text-xs mt-2">
                  AI adapts its knowledge and responses based on your business type
                </p>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Voice</label>
                <Select 
                  value={businessInfo.voice} 
                  onValueChange={(v) => setBusinessInfo({...businessInfo, voice: v})}
                >
                  <SelectTrigger className="input-industrial" data-testid="voice-select">
                    <SelectValue>
                      {selectedVoice && (
                        <span>{selectedVoice.label} - {selectedVoice.description}</span>
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    {VOICE_OPTIONS.map(voice => (
                      <SelectItem key={voice.value} value={voice.value} className="text-foreground hover:bg-[#27272A]">
                        <span className="flex flex-col">
                          <span className="font-medium">{voice.label}</span>
                          <span className="text-xs text-muted-foreground">{voice.description}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 rounded-sm bg-muted border border-border">
                <p className="text-[#10B981] text-sm font-medium mb-2">Multilingual Support</p>
                <p className="text-muted-foreground text-sm">
                  AI understands all languages including mixed speech (English + Russian, Uzbek, Spanish, etc.)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Business Info */}
          <Card className="card-modern">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground tracking-tight flex items-center gap-2">
                <Building className="w-5 h-5 text-[#007AFF]" />
                BUSINESS INFORMATION
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Business Name
                </label>
                <Input
                  value={businessInfo.name}
                  onChange={(e) => setBusinessInfo({...businessInfo, name: e.target.value})}
                  placeholder="Your Business Name"
                  className="input-industrial"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </label>
                <Input
                  value={businessInfo.phone}
                  onChange={(e) => setBusinessInfo({...businessInfo, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                  className="input-industrial"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </label>
                <Input
                  value={businessInfo.email}
                  onChange={(e) => setBusinessInfo({...businessInfo, email: e.target.value})}
                  placeholder="contact@yourbusiness.com"
                  className="input-industrial"
                  type="email"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </label>
                <Textarea
                  value={businessInfo.address}
                  onChange={(e) => setBusinessInfo({...businessInfo, address: e.target.value})}
                  placeholder="123 Main St, City, State 12345"
                  className="input-industrial"
                  rows={2}
                  data-testid="address-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Business Hours
                </label>
                <Input
                  value={businessInfo.hours}
                  onChange={(e) => setBusinessInfo({...businessInfo, hours: e.target.value})}
                  placeholder="Mon-Fri 8AM-6PM, Sat 9AM-3PM"
                  className="input-industrial"
                  data-testid="hours-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card className="card-modern">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground tracking-tight flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#F59E0B]" />
                SERVICES / MENU
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  List your services or menu items (one per line)
                </label>
                <Textarea
                  value={servicesText}
                  onChange={(e) => setServicesText(e.target.value)}
                  placeholder="Service 1&#10;Service 2&#10;Service 3"
                  className="input-industrial min-h-[200px]"
                  data-testid="services-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card className="card-modern">
            <CardHeader className="border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground tracking-tight">
                ADDITIONAL CONTEXT
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">
                  Any special info the AI should know about your business
                </label>
                <Textarea
                  value={businessInfo.additional_info}
                  onChange={(e) => setBusinessInfo({...businessInfo, additional_info: e.target.value})}
                  placeholder="Special offers, unique services, policies, etc."
                  className="input-industrial min-h-[200px]"
                  data-testid="additional-info-input"
                />
                <p className="text-[#6B7280] text-xs mt-2">
                  This info helps the AI give more accurate answers about your business
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button 
            type="submit" 
            className="btn-primary px-8 py-3"
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                SAVING...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                SAVE SETTINGS
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Preview Card */}
      <Card className="card-modern mt-8">
        <CardHeader className="border-b border-border">
          <CardTitle className="text-lg font-bold text-foreground tracking-tight">
            AI GREETING PREVIEW
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-muted rounded-sm p-4 border-l-4 border-[#007AFF]">
            <p className="text-foreground italic">
              "Hey, thanks for calling {businessInfo.name || 'your business'}. 
              {businessInfo.business_type === 'pizza' || businessInfo.business_type === 'restaurant' 
                ? " What can I get for you today?"
                : businessInfo.business_type === 'barber' || businessInfo.business_type === 'salon'
                ? " Looking to book an appointment?"
                : " How can I help you today?"
              }"
            </p>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <span className="text-muted-foreground text-sm">Voice:</span>
            <span className="text-foreground font-medium">{selectedVoice?.label}</span>
            <span className="text-muted-foreground text-sm">Type:</span>
            <span className="text-foreground font-medium">{selectedType?.label}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
